import {useAlive} from '@github-ui/use-alive'
import {throttle} from '@github/mini-throttle'
import useIsMounted from '@github-ui/use-is-mounted'
import {useCallback, useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BACKGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS, FOREGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS} from '../constants'

import {useStatusChecksPageData} from '../page-data/loaders/use-status-checks-page-data'
import {usePageVisibility} from './use-page-visibility'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'

export function useStatusChecksLiveUpdates(channel: string, pullRequestHeadSha: string): void {
  const {refetch, queryKey} = useStatusChecksPageData({pullRequestHeadSha})
  const isMounted = useIsMounted()
  const isPageVisible = usePageVisibility()
  const dedupeConcurrentRefetches = isFeatureEnabled('merge_status_checks_refetch_dedupe')

  const throttleTiming = isPageVisible
    ? FOREGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS
    : BACKGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS

  // Each hook instance owns its own throttle and refetches via its own query observer.
  const throttledRefetch = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          refetch()
        }
      }, throttleTiming),
    [isMounted, refetch, throttleTiming],
  )

  const handleNotification = useCallback(
    (data: {wait?: number}) => {
      // This creates a 2-10s throttle to prevent multiple refetches happening in the same < 500ms time window
      window.setTimeout(throttledRefetch, data.wait || 0)
    },
    [throttledRefetch],
  )

  useAlive(dedupeConcurrentRefetches ? '' : channel, handleNotification)

  useInvalidateOnAliveUpdate(dedupeConcurrentRefetches ? channel : '', queryKey, {throttleTimeout: throttleTiming})
}
